import {FunctionalComponent, h} from "@stencil/core";
import {CheckboxCell, TableHeader, TableIconHeader, TableSkeleton, TextCell, Topic} from "./notification-settings.sub";
import {i18nProvider} from "@trumpf/ux.i18n";
import type {Assets} from "../stores/userSettingsStore";

//TODO improve unconfiguredAssetsTooltip 'You have unconfigured assets*. By default, you will receive notifications from these assets until you explicitly set their notification status.'
//TODO ALL ASSETS CHECKBOX: fix tooltip text: 'toggle all (visible/filtered) assets'
//TODO fix ux-input-field error (waiting for fix by ui.lib team)

type Props = {
	topic: {headlineSlot: string; textSlot: string};
	table: {headerSlot: string};
	assets: Assets;
};

export const AssetsFC: FunctionalComponent<Props> = ({topic, table, assets}, footer) => {
	const {t} = i18nProvider;

	const hasUnconfiguredAssets = assets.data.some(({enabled}) => enabled === null);

	const filteredAssets = assets.data.filter(
		({name, id}) => name.toLowerCase().includes(assets.filter.toLowerCase()) || id.toLowerCase().includes(assets.filter.toLowerCase()),
	);

	const handleCheckboxAllChange = (checked: boolean) => {
		const newData = structuredClone(assets.data);
		filteredAssets.forEach((fAsset) => (newData.find((asset) => asset.id === fAsset.id).enabled = checked));
		assets.update({data: newData});
	};

	const handleCheckboxChange = (assetId: string, checked: boolean) => {
		const newData = structuredClone(assets.data);
		newData.find((asset) => asset.id === assetId).enabled = checked;
		assets.update({data: newData});
	};

	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return (
		<section class="u-marginBottom-40">
			<Topic headlineSlot={topic.headlineSlot} textSlot={topic.textSlot} />
			<ux-input-field
				placeholder={t("user-settings:assetsFilter")}
				disabled={assets.isLoading || assets.hasFailedGet || assets.hasFailedPost}
				value={assets.filter}
				onInput={(e: Event) => assets.update({filter: (e.target as HTMLInputElement).value})}
				clearable={true}
				onUxChange={(e: Event) => (e.target as HTMLInputElement).value === "" && assets.update({filter: ""})}
				// error={filteredAssets.length === 0 && assets.filter !== ""} //! not working as expected, shows error state only after losing focus
				class="u-marginBottom-25"
			/>
			<ux-scrollable-container scrollbar-placement="top-bottom" class="u-marginBottom-25 asset-table-container">
				<ux-table fullWidth mobileAccordion={false} fixed-col-index="0" max-fixed-col-width="64">
					<table>
						<thead>
							<tr>
								<th>
									<ux-checkbox
										disabled={filteredAssets.length === 0}
										checked={filteredAssets.length > 0 && filteredAssets.every((asset) => asset.enabled)}
										onChange={(e: Event) => handleCheckboxAllChange((e.target as HTMLInputElement).checked)}
									>
										<ux-icon name="square-info" disabled={filteredAssets.length === 0} weight="bold" />
										<ux-tooltip tooltip-target={"ux-icon[name=square-info]"} align="top">
											{t("user-settings:allAssetsText")}
										</ux-tooltip>
									</ux-checkbox>
								</th>
								{hasUnconfiguredAssets ? (
									<TableIconHeader headerSlot={table.headerSlot} icon="triangle-warning" tooltip={t("user-settings:unconfiguredAssets")} />
								) : (
									<TableHeader headerSlot={table.headerSlot} />
								)}
							</tr>
						</thead>
						<tbody>
							{assets.isLoading || assets.hasFailedGet ? (
								<TableSkeleton columns={2} rows={2} noAnimation={assets.hasFailedGet} />
							) : (
								filteredAssets.map(({name, id, enabled}) => (
									// eslint-disable-next-line @typescript-eslint/no-unsafe-return
									<tr>
										<CheckboxCell
											label={enabled === null ? "*" : ""}
											isChecked={enabled ?? true}
											onChange={(e) => handleCheckboxChange(id, (e.target as HTMLInputElement).checked)}
										/>
										<TextCell text={`${name} (${id})`} />
									</tr>
								))
							)}
						</tbody>
					</table>
				</ux-table>
			</ux-scrollable-container>
			{footer}
		</section>
	);
};
